import {
  getPrivateMessageUserCounts,
  updatePrivateMessageUser
} from '@moal/api'
import PropTypes from 'prop-types'
import { useEffect, useState } from 'react'
import { Outlet, useParams, useSearchParams } from 'react-router-dom'
import { useViewportHeight } from '../../../hooks/useViewportHeight'
import { setPageTitle, setPageTitleCounter } from '../../../utils/utilities'
import Tooltip from '../../Utilities/Tooltip'
import { AllUsers, PinnedUsers, UsersAwaitingReply } from './UserLists'

const USERS_AWAITNG_REPLY = 'Users Awaiting Reply'
const ALL_USERS = 'All Users'
const PINNED_USERS = 'Pinned Users'

const PrivateMessages = () => {
  useViewportHeight()
  setPageTitle('Private Messages')
  const [searchParams] = useSearchParams()
  const searchId = searchParams.get('id') || null

  const [active, setActive] = useState(
    searchId ? ALL_USERS : USERS_AWAITNG_REPLY
  )
  const { userId } = useParams()
  const [userCounts, setUserCounts] = useState({
    total: 0,
    awaitingReply: 0,
    pinned: 0
  })

  useEffect(() => {
    fetchUserCounts()
  }, [])

  const fetchUserCounts = () => {
    getPrivateMessageUserCounts({
      token: localStorage.getItem('authToken')
    }).then((res) => {
      setUserCounts(res.data)
    })
  }

  useEffect(() => {
    setPageTitleCounter(userCounts.awaitingReply, 'Private Messages')
  }, [userCounts])

  const onFocus = () => {
    setPageTitle('Private Messages')
  }

  const onBlur = () => {
    setPageTitleCounter(userCounts.awaitingReply, 'Private Messages')
  }

  useEffect(() => {
    window.addEventListener('focus', onFocus)
    window.addEventListener('blur', onBlur)
    return () => {
      window.removeEventListener('focus', onFocus)
      window.removeEventListener('blur', onBlur)
    }
  }, [userCounts])

  const handlePinnedUserClick = async (pmUser, callback) => {
    const response = await updatePrivateMessageUser({
      token: localStorage.getItem('authToken'),
      id: pmUser._id,
      data: { pinned: !pmUser.pinned }
    })
    if (response && response.status === 200) {
      callback()
    }
  }

  return (
    <div className="flex flex-row w-full h-full gap-2">
      <div
        className={`flex-col overflow-hidden w-full ${
          userId ? 'lg:flex hidden' : 'flex'
        }`}
      >
        <div className="flex items-center mb-5">
          <h2 className="mr-3">Private Messages</h2>
          <Tooltip>
            View messages from users to moderators. Use the tabs to filter by
            users awaiting replies, pinned users, or all users. Click a username
            to start a direct chat.
          </Tooltip>
        </div>
        <PrivateMessagesNav
          active={active}
          handleClick={setActive}
          userCounts={userCounts}
        />

        <div className="overflow-hidden h-full">
          {active === USERS_AWAITNG_REPLY && (
            <UsersAwaitingReply
              fetchUserCounts={fetchUserCounts}
              handlePinnedUserClick={handlePinnedUserClick}
            />
          )}
          {active === ALL_USERS && (
            <AllUsers
              fetchUserCounts={fetchUserCounts}
              handlePinnedUserClick={handlePinnedUserClick}
              searchId={searchId}
            />
          )}
          {active === PINNED_USERS && (
            <PinnedUsers
              fetchUserCounts={fetchUserCounts}
              handlePinnedUserClick={handlePinnedUserClick}
            />
          )}
        </div>
      </div>

      <div className={`flex flex-col w-full ${userId ? 'flex' : 'hidden'}`}>
        <Outlet />
      </div>
    </div>
  )
}

const PrivateMessagesNav = ({ active, handleClick, userCounts }) => {
  return (
    <div className="flex flex-col justify-between w-full pb-2">
      <ul className="flex flex-col w-full gap-2">
        <li className="w-full">
          <button
            onClick={() => handleClick(USERS_AWAITNG_REPLY)}
            className={`chat-nav-button flex items-center pl-1.5 pr-3 gap-2.5 ${
              active === USERS_AWAITNG_REPLY
                ? 'chat-nav-button-active'
                : 'button-outline'
            }`}
          >
            <span className="bg-brand-primary-200 dark:bg-brand-primary-900 px-2 py-0.5 rounded-sm text-black dark:text-white">
              {userCounts.awaitingReply}
            </span>
            <span>{USERS_AWAITNG_REPLY}</span>
          </button>
        </li>
        <li className="w-full">
          <button
            onClick={() => handleClick(PINNED_USERS)}
            className={`chat-nav-button flex items-center pl-1.5 pr-3 gap-2.5 ${
              active === PINNED_USERS
                ? 'chat-nav-button-active'
                : 'button-outline'
            }`}
          >
            <span className="bg-brand-primary-200 dark:bg-brand-primary-900 px-2 py-0.5 rounded-sm text-black dark:text-white">
              {userCounts.pinned}
            </span>
            <span>{PINNED_USERS}</span>
          </button>
        </li>
        <li className="w-full">
          <button
            onClick={() => handleClick(ALL_USERS)}
            className={`chat-nav-button flex items-center pl-1.5 pr-3 gap-2.5 ${
              active === ALL_USERS ? 'chat-nav-button-active' : 'button-outline'
            }`}
          >
            <span className="bg-brand-primary-200 dark:bg-brand-primary-900 px-2 py-0.5 rounded-sm text-black dark:text-white">
              {userCounts.total}
            </span>
            <span>{ALL_USERS}</span>
          </button>
        </li>
      </ul>
    </div>
  )
}

PrivateMessagesNav.propTypes = {
  active: PropTypes.string.isRequired,
  handleClick: PropTypes.func.isRequired,
  userCounts: PropTypes.shape({
    total: PropTypes.number.isRequired,
    awaitingReply: PropTypes.number.isRequired,
    pinned: PropTypes.number.isRequired
  }).isRequired
}

export default PrivateMessages
